// URL ↔ TopSellersFilterState serialization. Both directions are pure / SSR-safe.
//
// URL schema:
//   ?genres=action,rpg
//   ?categories=1,9
//   ?tags=4182,3859
//   ?languages=0,3
//   ?price_from=0          (price in cents, int)
//   ?price_to=2000
//   ?rating_from=70        (percent 0-100)
//   ?rating_to=100
//   ?discount_from=10      (percent 0-100)
//   ?discount_to=90
//   ?release_from=2024-01-01  (YYYY-MM-DD)
//   ?release_to=2024-12-31
//   ?upcoming=1
//   ?cd=1,2,3              (content descriptor ids)
//   ?types=game,dlc        (app types)
//   ?reviews_from=1000     (review count)
//   ?reviews_to=50000
//   ?followers_from=1000   (latest follower count)
//   ?followers_to=100000
//   ?ccu_from=100          (players right now)
//   ?ccu_to=10000
//   ?peak_ccu_from=1000    (all-time peak CCU)
//   ?peak_ccu_to=500000
//   ?publishers=Valve,KRAFTON%252C%2520Inc.  (per-name URI-encoded, comma-joined)
//   ?developers=Re-Logic
//   ?os=windows,mac,linux  (AND — app must support all picked)
//   ?status=released|early_access|coming_soon
//   ?ea_exit_from=2026-07-01   (scheduled EA-exit window, YYYY-MM-DD)
//   ?ea_exit_to=2026-12-31
//   ?ach_from=1            (achievements count; 1 = "has achievements")
//   ?ach_to=50
//   ?wl_rank_from=1        (wishlist rank, top-tracked only)
//   ?wl_rank_to=100
//
// Param names are short + URL-safe. Empty/default values are omitted so the
// URL stays clean when no filters are active.

import type { TopSellersFilterState } from "../types";
import { getDefaultFilterState } from "./filterUtils";

// Reserved keys belonging to other URL responsibilities (page, sort, etc.).
// Used by mergeFilterParamsIntoSearch to preserve them when rewriting.
export const FILTER_URL_KEYS = [
  "genres",
  "categories",
  "tags",
  "languages",
  "price_from",
  "price_to",
  "rating_from",
  "rating_to",
  "discount_from",
  "discount_to",
  "release_from",
  "release_to",
  "upcoming",
  "cd",
  "types",
  "reviews_from",
  "reviews_to",
  "followers_from",
  "followers_to",
  "ccu_from",
  "ccu_to",
  "peak_ccu_from",
  "peak_ccu_to",
  "publishers",
  "developers",
  "os",
  "status",
  "ea_exit_from",
  "ea_exit_to",
  "ach_from",
  "ach_to",
  "wl_rank_from",
  "wl_rank_to",
] as const;

type ReadOnlyParams = URLSearchParams | ReadonlyURLSearchParamsLike;

interface ReadonlyURLSearchParamsLike {
  get(name: string): string | null;
}

function parseCsvNumbers(v: string | null): number[] | null {
  if (!v) return null;
  const nums = v
    .split(",")
    .map((x) => Number(x.trim()))
    .filter((n) => Number.isFinite(n));
  return nums.length ? nums : null;
}

function parseCsvStrings(v: string | null): string[] | null {
  if (!v) return null;
  const arr = v
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  return arr.length ? arr : null;
}

function parseInt0(v: string | null): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseDate(v: string | null): string | null {
  if (!v) return null;
  // Strict YYYY-MM-DD — don't trust arbitrary strings.
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

// Company names may contain commas ("KRAFTON, Inc.") — each name is
// URI-encoded before the comma-join, so the comma stays an unambiguous
// delimiter. decodeURIComponent can throw on malformed %-sequences from
// hand-edited URLs — drop those entries instead of crashing SSR.
function parseCsvEncodedStrings(v: string | null): string[] | null {
  if (!v) return null;
  const arr = v
    .split(",")
    .map((x) => {
      try {
        return decodeURIComponent(x).trim();
      } catch {
        return "";
      }
    })
    .filter(Boolean);
  return arr.length ? arr : null;
}

const OS_VALUES = new Set(["windows", "mac", "linux"]);

function parseOs(v: string | null): string[] | null {
  const arr = parseCsvStrings(v)?.filter((x) => OS_VALUES.has(x)) ?? null;
  return arr?.length ? arr : null;
}

type ReleaseStatus = "released" | "full_release" | "early_access" | "coming_soon";
const STATUS_VALUES = new Set<ReleaseStatus>([
  "released",
  "full_release",
  "early_access",
  "coming_soon",
]);

function parseStatus(v: string | null): ReleaseStatus | null {
  return v && STATUS_VALUES.has(v as ReleaseStatus) ? (v as ReleaseStatus) : null;
}

/** Read filter state from URL params (SSR-safe — accepts plain object too). */
export function parseFiltersFromSearchParams(
  params: ReadOnlyParams,
): TopSellersFilterState {
  return {
    genres: parseCsvStrings(params.get("genres")),
    categories: parseCsvNumbers(params.get("categories")),
    tags: parseCsvNumbers(params.get("tags")),
    languages: parseCsvNumbers(params.get("languages")),
    priceFrom: parseInt0(params.get("price_from")),
    priceTo: parseInt0(params.get("price_to")),
    ratingFrom: parseInt0(params.get("rating_from")),
    ratingTo: parseInt0(params.get("rating_to")),
    discountFrom: parseInt0(params.get("discount_from")),
    discountTo: parseInt0(params.get("discount_to")),
    releaseFrom: parseDate(params.get("release_from")),
    releaseTo: parseDate(params.get("release_to")),
    upcomingOnly: params.get("upcoming") === "1",
    contentDescriptorIds: parseCsvNumbers(params.get("cd")),
    appTypes: parseCsvStrings(params.get("types")),
    reviewsFrom: parseInt0(params.get("reviews_from")),
    reviewsTo: parseInt0(params.get("reviews_to")),
    followersFrom: parseInt0(params.get("followers_from")),
    followersTo: parseInt0(params.get("followers_to")),
    ccuFrom: parseInt0(params.get("ccu_from")),
    ccuTo: parseInt0(params.get("ccu_to")),
    peakCcuFrom: parseInt0(params.get("peak_ccu_from")),
    peakCcuTo: parseInt0(params.get("peak_ccu_to")),
    publishers: parseCsvEncodedStrings(params.get("publishers")),
    developers: parseCsvEncodedStrings(params.get("developers")),
    os: parseOs(params.get("os")),
    releaseStatus: parseStatus(params.get("status")),
    eaExitFrom: parseDate(params.get("ea_exit_from")),
    eaExitTo: parseDate(params.get("ea_exit_to")),
    achievementsFrom: parseInt0(params.get("ach_from")),
    achievementsTo: parseInt0(params.get("ach_to")),
    wishlistRankFrom: parseInt0(params.get("wl_rank_from")),
    wishlistRankTo: parseInt0(params.get("wl_rank_to")),
  };
}

/**
 * Build the filter-related URLSearchParams subset. Default/empty values are
 * omitted so the URL stays minimal.
 */
export function serializeFiltersToParams(
  state: TopSellersFilterState,
): URLSearchParams {
  const out = new URLSearchParams();
  if (state.genres?.length) out.set("genres", state.genres.join(","));
  if (state.categories?.length)
    out.set("categories", state.categories.join(","));
  if (state.tags?.length) out.set("tags", state.tags.join(","));
  if (state.languages?.length) out.set("languages", state.languages.join(","));
  if (state.priceFrom != null) out.set("price_from", String(state.priceFrom));
  if (state.priceTo != null) out.set("price_to", String(state.priceTo));
  if (state.ratingFrom != null) out.set("rating_from", String(state.ratingFrom));
  if (state.ratingTo != null) out.set("rating_to", String(state.ratingTo));
  if (state.discountFrom != null)
    out.set("discount_from", String(state.discountFrom));
  if (state.discountTo != null) out.set("discount_to", String(state.discountTo));
  if (state.releaseFrom) out.set("release_from", state.releaseFrom);
  if (state.releaseTo) out.set("release_to", state.releaseTo);
  if (state.upcomingOnly) out.set("upcoming", "1");
  if (state.contentDescriptorIds?.length)
    out.set("cd", state.contentDescriptorIds.join(","));
  if (state.appTypes?.length) out.set("types", state.appTypes.join(","));
  if (state.reviewsFrom != null)
    out.set("reviews_from", String(state.reviewsFrom));
  if (state.reviewsTo != null) out.set("reviews_to", String(state.reviewsTo));
  if (state.followersFrom != null)
    out.set("followers_from", String(state.followersFrom));
  if (state.followersTo != null)
    out.set("followers_to", String(state.followersTo));
  if (state.ccuFrom != null) out.set("ccu_from", String(state.ccuFrom));
  if (state.ccuTo != null) out.set("ccu_to", String(state.ccuTo));
  if (state.peakCcuFrom != null)
    out.set("peak_ccu_from", String(state.peakCcuFrom));
  if (state.peakCcuTo != null) out.set("peak_ccu_to", String(state.peakCcuTo));
  if (state.publishers?.length)
    out.set("publishers", state.publishers.map(encodeURIComponent).join(","));
  if (state.developers?.length)
    out.set("developers", state.developers.map(encodeURIComponent).join(","));
  if (state.os?.length) out.set("os", state.os.join(","));
  if (state.releaseStatus) out.set("status", state.releaseStatus);
  if (state.eaExitFrom) out.set("ea_exit_from", state.eaExitFrom);
  if (state.eaExitTo) out.set("ea_exit_to", state.eaExitTo);
  if (state.achievementsFrom != null)
    out.set("ach_from", String(state.achievementsFrom));
  if (state.achievementsTo != null)
    out.set("ach_to", String(state.achievementsTo));
  if (state.wishlistRankFrom != null)
    out.set("wl_rank_from", String(state.wishlistRankFrom));
  if (state.wishlistRankTo != null)
    out.set("wl_rank_to", String(state.wishlistRankTo));
  return out;
}

/**
 * Merge a new filter state into an existing search-params string while
 * preserving any other params (page, sort, tab, etc.). Returns a new
 * query-string fragment without the leading "?".
 */
export function mergeFilterParamsIntoSearch(
  existing: string | URLSearchParams,
  next: TopSellersFilterState,
): string {
  const merged =
    typeof existing === "string"
      ? new URLSearchParams(existing)
      : new URLSearchParams(existing);

  // Wipe stale filter params first.
  for (const key of FILTER_URL_KEYS) merged.delete(key);

  // Write current filter state.
  const filterParams = serializeFiltersToParams(next);
  for (const [k, v] of filterParams.entries()) merged.set(k, v);

  return merged.toString();
}

/**
 * True if the parsed filters differ from getDefaultFilterState() — used to
 * decide whether the parsed URL actually contained any filters.
 */
export function filtersFromUrlAreActive(state: TopSellersFilterState): boolean {
  const def = getDefaultFilterState();
  return JSON.stringify(state) !== JSON.stringify(def);
}

/**
 * Server-side: parse filter state from a Next.js awaited `searchParams` object
 * (`{ [key]: string | string[] | undefined }`). Returns the parsed filters plus
 * `active` (whether any filter param is present). Use in server components to
 * SSR the *filtered* list when filter params are in the URL — eliminates the
 * unfiltered-flash on shareable links and the broken/eternal-loader state on
 * back-navigation + reload of a filtered URL.
 */
export function parseFiltersFromNextSearchParams(searchParams: {
  [key: string]: string | string[] | undefined;
}): { filters: TopSellersFilterState; active: boolean } {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(searchParams)) {
    if (Array.isArray(v)) {
      for (const x of v) sp.append(k, x);
    } else if (v != null) {
      sp.set(k, v);
    }
  }
  const filters = parseFiltersFromSearchParams(sp);
  return { filters, active: filtersFromUrlAreActive(filters) };
}
